import { Button } from '@atlas/ui-primitives'
import { CAMPAIGN_STATUS_LABELS, CampaignStatus } from '@/domain/campaign.types'

export type CampaignLifecycleViewState = 'idle' | 'pending' | 'success' | 'invalid-transition' | 'conflict' | 'failure'

export interface CampaignLifecyclePanelProps {
	status: CampaignStatus
	/** Literal available actions supplied by the lifecycle capability — never derived, computed, or fabricated here. */
	availableActions: readonly CampaignStatus[]
	canTransition: boolean
	viewState: CampaignLifecycleViewState
	selectedNextStatus: CampaignStatus | null
	onSelectNextStatus: (status: CampaignStatus) => void
	onSubmitTransition: () => void
	onReload?: () => void
}

const ACTION_BASE =
	'rounded-ds-full border px-3 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-border disabled:opacity-50'
const ACTION_SELECTED = 'border-ds-primary bg-ds-primary text-ds-primary-foreground'
const ACTION_IDLE = 'border-ds-border bg-ds-surface-elevated text-ds-text-muted'

/**
 * The compact lifecycle presentation composed inside the detail heading area.
 * Every supplied available action renders as its own inline control, perceivable
 * without opening any menu, popover, or disclosure, and the controls wrap rather
 * than overflow. Selecting an action never changes status: the explicit transition
 * action submits it.
 *
 * All lifecycle data arrives as props. The panel derives no action from the current
 * status and holds no transition table, matrix, or state machine — it renders
 * exactly the actions it was supplied. When `canTransition` is false every supplied
 * action stays visibly rendered and disabled, so the lifecycle information remains
 * read-only rather than absent. The current status itself is presented by the
 * heading's status badge.
 */
export function CampaignLifecyclePanel({
	status,
	availableActions,
	canTransition,
	viewState,
	selectedNextStatus,
	onSelectNextStatus,
	onSubmitTransition,
	onReload,
}: CampaignLifecyclePanelProps) {
	const isArchived = status === CampaignStatus.ARCHIVED
	const isPending = viewState === 'pending'

	return (
		<section aria-label="Lifecycle" className="min-w-0 space-y-2">
			<div className="flex min-w-0 flex-wrap items-center gap-2">
				{isArchived && <span className="text-xs font-medium text-ds-danger">This Campaign is archived.</span>}

				{availableActions.length > 0 && (
					<div className="flex flex-wrap items-center gap-1.5 rounded-ds-md border border-ds-border bg-ds-surface p-1">
						{availableActions.map((action) => (
							<button
								key={action}
								type="button"
								aria-pressed={selectedNextStatus === action}
								disabled={!canTransition || isPending}
								onClick={() => onSelectNextStatus(action)}
								className={`${ACTION_BASE} ${selectedNextStatus === action ? ACTION_SELECTED : ACTION_IDLE}`}
							>
								{CAMPAIGN_STATUS_LABELS[action]}
							</button>
						))}
					</div>
				)}

				<Button
					size="sm"
					isDisabled={!canTransition || selectedNextStatus === null || isPending}
					onPress={onSubmitTransition}
				>
					{isPending ? 'Saving…' : 'Save transition'}
				</Button>
			</div>

			{viewState === 'invalid-transition' && (
				<p role="alert" className="text-sm text-ds-danger">
					That transition isn't allowed. The current status was not changed.
				</p>
			)}
			{viewState === 'conflict' && (
				<div role="alert" className="flex flex-wrap items-center gap-2">
					<p className="text-sm text-ds-danger">This Campaign changed since it was loaded.</p>
					<Button variant="outline" size="sm" onPress={onReload}>
						Reload
					</Button>
				</div>
			)}
			{viewState === 'failure' && (
				<p role="alert" className="text-sm text-ds-danger">
					Something went wrong submitting that transition.
				</p>
			)}
		</section>
	)
}
